import { readCommunityUtmDetails } from '@common/analytics-layer/utils/utm-session'
import { isAnalyticsBotTraffic, readAnalyticsBotSignals } from '@common/analytics-layer/utils/is-analytics-bot.utils'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { AnalyticsService } from '../services/analytics.service'
import type { CommunityTrafficPageId, TrackTrafficPageViewInput } from '../types/analytics.api'

const CONTENT_UNAVAILABLE_SEGMENT = 'content-unavailable'

let lastTrafficTrackKey: string | null = null

/** Test hook — resets session dedupe between cases. */
export function resetCommunityTrafficPageViewDedupe(): void {
  lastTrafficTrackKey = null
}

export function resolveTrafficPageId(path: string | null | undefined): CommunityTrafficPageId | null {
  if (!path?.trim()) return null

  const normalized = path.replace(/\/+$/, '')
  if (normalized.includes(`/${CONTENT_UNAVAILABLE_SEGMENT}`)) return null

  if (normalized.includes('/conversations')) return 'conversations'
  if (normalized.includes('/about')) return 'about'
  if (normalized.includes('/learning')) return 'learning'
  if (normalized.includes('/settings')) return 'settings'
  if (normalized.includes('/members')) return 'members'
  if (normalized.includes('/events')) return 'events'
  if (normalized.includes('/leaderboard')) return 'leaderboard'

  return 'home'
}

/**
 * Fire-and-forget traffic page-view ingest for member-facing group tabs.
 */
export function trackCommunityTrafficPageView(input: TrackTrafficPageViewInput, service = new AnalyticsService()): void {
  if (typeof window === 'undefined') return

  const groupId = input.groupId?.trim()
  const pageId = input.pageId?.split(/[?#]/)[0]?.trim()
  if (!groupId || !pageId) return

  const locationId = input.locationId?.trim() || useLocation().store.locationId?.trim()
  if (!locationId) return

  const trackKey = `${groupId}:${pageId}`
  if (lastTrafficTrackKey === trackKey) return
  lastTrafficTrackKey = trackKey

  if (isAnalyticsBotTraffic(readAnalyticsBotSignals())) return

  const utm = readCommunityUtmDetails()
  const pageUrl = (input.pageUrl ?? window.location.pathname).trim()
  const body: Record<string, string> = {
    pageId,
    clientTimestamp: new Date().toISOString(),
  }
  const utmSource = typeof utm?.utm_source === 'string' ? utm.utm_source.trim() : ''
  const utmReferrer = typeof utm?.utmReferrer === 'string' ? utm.utmReferrer.trim() : ''
  if (utmSource) body.utmSource = utmSource
  if (utmReferrer) body.utmReferrer = utmReferrer
  if (pageUrl) body.pageUrl = pageUrl

  const { logger } = useLogger()
  const endpoint = `/community-analytics/${locationId}/groups/${groupId}/traffic/page-view`

  void (async () => {
    try {
      await service.post(endpoint, body, { keepalive: true, groupId, locationId })
    } catch (error) {
      logger.error('[trackCommunityTrafficPageView] failed', {
        error: error instanceof Error ? error : new Error(String(error)),
        payload: { pageId, groupId, locationId },
      })
    }
  })()
}
